import { Alert, DialogContentText } from '@mui/material';
import { DatePickerAdapter } from 'components/enhanced/form/adapters/date-time/DatePickerAdapter';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { required } from 'components/helpers/validators';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import dayjs from 'dayjs';
import { orderLineService } from 'features/inventory/purchase-orders/api/order-line.service';
import { orderService } from 'features/inventory/purchase-orders/api/order.service';
import { PURCHASE_ORDER_LINE_STATUS_FILTER_IN_ARRIVO } from 'features/inventory/purchase-orders/constants';
import { stockQuantityLocationService } from 'features/inventory/stock/api/stock-quantity-location.service';
import { stockQuantityService } from 'features/inventory/stock/api/stock-quantity.service';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import { ITenantSettings } from 'features/settings/tenant-settings/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  contractLineService,
  locationService,
  productService,
  tenantSettingsService
} from 'shared/services';
import { productWarehouseService } from 'features/inventory/warehouses/api/product-warehouse.service';
import { Option } from 'shared/types/general';
import { IOptionStockQuantity } from '../../../stock/types';
import { movementReasonService } from '../../api/movement-reason.service';
import { DateTimePickerAdapter, CheckboxAdapter, EnhancedDialog } from 'components/enhanced';
import { toast } from 'shared/utils';

interface ManualMovementProps {
  onSubmit: any;
  closeManualMovements: () => void;
}

interface ContractLineOption {
  id: number;
  code: string;
  unit_price: number | string;
}

function ManualMovement({ onSubmit, closeManualMovements }: ManualMovementProps) {
  const { t } = useTranslation();

  const defaultWarehouse = null;

  const [contractLines, setContractLines] = useState<ContractLineOption[]>([]);
  const [batches, setBatches] = useState<IOptionStockQuantity[]>([]);
  const [locations, setLocations] = useState<Option[]>([]);
  const [movementReasons, setMovementReasons] = useState<Option[]>([]);
  const [products, setProducts] = useState<Option[]>([]);
  const [warehouses, setWarehouses] = useState<Option[]>([]);

  const [expiration, setExpiration] = useState<Date>();
  const [orders, setOrders] = useState<Option[]>([]);
  const [order, setOrder] = useState<Option | null>(null);
  const [product, setProduct] = useState<Option | null>(null);
  const [tenantSettings, setTenantSettings] = useState<ITenantSettings>();
  const [type, setType] = useState<Option | null>(null);
  const [warehouse, setWarehouse] = useState<Option | null>(null);

  const [purchaseReqNotes, setPurchaseReqNotes] = useState('');
  const [defaultLocation, setDefaultLocation] = useState<Option | null>(null);

  const [refresh, setRefresh] = useState(false); // Per forzare il reset del componente AutocompleteAdapterProduct
  const [batchConfirmationOpen, setBatchConfirmationOpen] = useState(false);
  const [pendingMovementResults, setPendingMovementResults] = useState<any>(null);
  const tenant = process.env.REACT_APP_TENANT;

  const handleConfirmSelectedBatch = () => {
    if (!pendingMovementResults) {
      setBatchConfirmationOpen(false);
      return;
    }

    onSubmit(pendingMovementResults);
    setPendingMovementResults(null);
    setBatchConfirmationOpen(false);
    // lasciarla commentata per evitare che il form si chiuda automaticamente dopo l'invio, in modo da permettere ulteriori movimenti senza dover riaprire il form
    // closeManualMovements();
  };

  const handleCloseBatchConfirmation = () => {
    setPendingMovementResults(null);
    setBatchConfirmationOpen(false);
  };

  // 1. EFFETTO INIZIALE: caricamento base
  useEffect(() => {
    tenantSettingsService
      .get(1)
      .then(setTenantSettings)
      .catch(() => setTenantSettings(undefined));
    warehouseService
      .getAllBaseInformation()
      .then((res) => {
        if (res && Array.isArray(res)) {
          setWarehouses(res);
        } else {
          setWarehouses([]);
        }
      })
      .catch(() => setWarehouses([]));
    movementReasonService
      .getAllPaginated()
      .then((res) => setMovementReasons(res?.results || []))
      .catch(() => setMovementReasons([]));
  }, []);

  // 2. Prodotti per magazzino (products-summary esclude dismessi/bloccati nel magazzino)
  useEffect(() => {
    if (!warehouse?.id) {
      setProducts([]);
      setProduct(null);
      return;
    }

    const params: any = {
      warehouse_ids: warehouse.id
    };

    if (order?.id) {
      params.order_id = order.id; // Filtro prodotti legati all'ordine
      params.has_order_line = true;
      setProducts([]); // resetta i prodotti per evitare confusione
    }

    productService
      .getAllBaseInformation(params)
      .then((res) => {
        if (res && Array.isArray(res)) {
          setProducts(res);
        } else {
          setProducts([]);
        }
        // Mantieni il prodotto selezionato se ancora valido
        setProduct(null);
        setRefresh(!refresh);
      })
      .catch(() => {
        setProducts([]);
        setProduct(null);
        setRefresh(!refresh);
      });
  }, [warehouse?.id, order?.id]);

  // 2b. Piazza predefinita di carico per (prodotto, magazzino): prefill modificabile
  useEffect(() => {
    if (!product?.id || !warehouse?.id) {
      setDefaultLocation(null);
      return;
    }
    productWarehouseService
      .getAllBaseInformation({ product: product.code, warehouse: warehouse.code })
      .then((res) => {
        const pw = Array.isArray(res) ? res[0] : res;
        const defaultCode = pw?.default_location;
        if (defaultCode) {
          const matching = locations.find((loc) => loc.code === defaultCode);
          if (matching) {
            setDefaultLocation(matching);
            return;
          }
        }
        setDefaultLocation(null);
      })
      .catch(() => setDefaultLocation(null));
  }, [product?.id, warehouse?.id, locations]);

  // 3️⃣ Caricamento ordini "aperti" (solo all'avvio o quando cambia direzione del movimento)
  useEffect(() => {
    if (!type?.id) return;

    const params: any = { status: 1 };
    // PER MESTRE: solo per ordini in uno specifico magazzino
    if (tenant !== 'ROMA2') {
      if (warehouse?.id) {
        params.warehouse = warehouse.code;
      }
    }
    orderService
      .openOrders(1000, 0, params)
      .then((res) => {
        if (res?.results && Array.isArray(res.results)) {
          setOrders(
            res.results.map((v) => ({
              id: v.id,
              code: v.ref,
              description: v.description
            }))
          );
        } else {
          setOrders([]);
        }
      })
      .catch(() => setOrders([]));
  }, [type?.id, warehouse?.id]);

  // 4️⃣ EFFETTO: lazy loading delle piazze (solo se serve)
  useEffect(() => {
    if (!warehouse?.id) return;
    if (!type || type['direction'] !== 'IN') return; // esempio: carica piazze solo in ingresso
    if (locations.length > 0) return; // evita chiamate duplicate

    locationService
      .getAllBaseInformation({ warehouse_id: warehouse.id })
      .then(setLocations)
      .catch(() => setLocations([]));
  }, [warehouse?.id, type?.id]);

  // 5️⃣ EFFETTO: caricamento batches solo se tutti i prerequisiti sono presenti
  useEffect(() => {
    if (!(type && type['direction'] === 'OUT' && product?.id && warehouse?.id)) {
      setBatches([]);
      return;
    }

    const params = {
      product__id: product.id,
      warehouse__code: warehouse.code,
      quantity__gt: 0
    };

    stockQuantityService
      .getUnloadStocks(1000, 0, params)
      .then((res) => {
        if (res?.results && Array.isArray(res.results)) {
          const firstBatchData = res.results
            .filter((v) => v.free_quantity > 0)
            .map((v) => {
              let description = v.product_batch;
              if (v.expiration_date) description += `; Scad: ${v.expiration_date}`;
              description += `; Qty: ${v.free_quantity}`;
              return {
                id: crypto.randomUUID(),
                code: description,
                product_batch: v.product_batch,
                expiration_date: v.expiration_date,
                quantity: v.total_quantity
              };
            });

          setBatches(firstBatchData);

          // seconda chiamata API per location
          return stockQuantityLocationService.getUnloadStockLocation(1000, 0, params);
        } else {
          setBatches([]);
          return Promise.resolve({ results: [] });
        }
      })
      .then((res) => {
        if (res?.results && Array.isArray(res.results)) {
          const secondBatchData = res.results.map((v) => {
            let description = v.product_batch;
            if (v.expiration_date) description += `; Scad: ${v.expiration_date}`;
            description += `; Qty: ${v.quantity}; ${v.location__code}`;
            return {
              id: crypto.randomUUID(),
              code: description,
              product_batch: v.product_batch,
              expiration_date: v.expiration_date,
              location: v.location__code,
              qty: v.quantity
            };
          });

          setBatches((prev) => [...prev, ...secondBatchData]);
        }
      })
      .catch(() => setBatches([]));
  }, [type?.id, product?.id, warehouse?.id]);

  // ✅ 6️⃣ EFFETTO: caricamento contract lines (solo quando ci sono sia magazzino che prodotto)
  useEffect(() => {
    if (!(product?.id && warehouse?.id)) {
      setContractLines([]);
      return;
    }

    const params = {
      product_id: product.id,
      warehouse: warehouse.id
    };

    contractLineService
      .getAllBaseInformation(params)
      .then((res) => {
        if (res && Array.isArray(res)) {
          setContractLines(
            res.map((cl) => ({
              id: cl.id,
              code: `${cl.code} - ${cl.supplier}`,
              unit_price: cl.unit_price
            }))
          );
        } else {
          setContractLines([]);
        }
      })
      .catch(() => setContractLines([]));
  }, [product?.id, warehouse?.id]);

  // ✅ 7️⃣ EFFETTO: caricamento note da richiesta di acquisto
  useEffect(() => {
    if (order?.id && product?.id && warehouse?.id) {
      orderLineService
        .getAllPaginated(1, 0, {
          ref: order.code,
          product: product.code,
          warehouse: warehouse.code,
          status: PURCHASE_ORDER_LINE_STATUS_FILTER_IN_ARRIVO
        })
        .then((res) => {
          if (res?.results?.length > 0) {
            setPurchaseReqNotes(res.results[0]?.notes ?? '');
          } else {
            setPurchaseReqNotes('');
          }
        })
        .catch(() => setPurchaseReqNotes(''));
    } else {
      setPurchaseReqNotes('');
    }
  }, [order?.id, product?.id, warehouse?.id]);

  const submitForm = (values) => {
    productService.get(product.id).then((res) => {
      const selectedBatch: IOptionStockQuantity = batches.find(
        (batch) => batch.id === values.product_batch
      );
      const selectedBatchExpiration = selectedBatch?.expiration_date;

      const earlierExpirationBatches =
        type?.['direction'] === 'OUT' && selectedBatchExpiration
          ? Array.from(
              new Set(
                batches
                  .filter(
                    (batch) =>
                      batch.product_batch &&
                      batch.expiration_date &&
                      dayjs(batch.expiration_date).isBefore(dayjs(selectedBatchExpiration))
                  )
                  .map((batch) => batch.product_batch as string)
              )
            )
          : [];

      // Genero il "BLOB MOVIMENTO" contenenente i dati da inviare al componente padre
      const movement = {
        ...values,
        description: res.description,
        product_batch: selectedBatch
          ? selectedBatch.product_batch
          : values.product_batch || 'NO_BATCH',
        warehouse: warehouse?.code || '',
        expiration_date:
          type?.['direction'] === 'OUT'
            ? selectedBatch?.expiration_date || values.expiration_date || null
            : values.expiration_date || null,
        unit_price: contractLines.length === 1 ? contractLines[0].unit_price || 0 : undefined,
        contract_line: contractLines.length === 1 ? contractLines[0].id : undefined,
        note_location:
          type['direction'] === 'IN'
            ? values.notes_location || ''
            : selectedBatch?.location || values.location || ''
      };

      // WARNING to
      const send_warning_stock_not_matching =
        Boolean(selectedBatch) && movement.moved_quantity > selectedBatch.quantity;

      const results = {
        movement: movement,
        batches: earlierExpirationBatches.length > 0 ? earlierExpirationBatches : undefined,
        warning_stock: send_warning_stock_not_matching
      };

      if (results.warning_stock) {
        toast.error(
          t('toasts.movement-quantity-not-found', {
            moved_quantity: results.movement.moved_quantity
          })
        );
        return;
      }

      if (results.batches?.length) {
        setPendingMovementResults(results);
        setBatchConfirmationOpen(true);
        return;
      }

      onSubmit(results);
      // lasciarla commentata per evitare che il form si chiuda automaticamente dopo l'invio, in modo da permettere ulteriori movimenti senza dover riaprire il form
      // closeManualMovements();
    });
  };

  return (
    <>
      <EnhancedForm onSubmit={submitForm}>
        <div className="flex flex-wrap my-4">
          {tenantSettings &&
            expiration &&
            tenantSettings.days_before_expiration_alert &&
            dayjs(expiration).diff(dayjs(), 'days') <=
              tenantSettings.days_before_expiration_alert && (
              <Alert severity="error" className="w-full">
                {t(`alerts.product-close-to-expiration`)}
              </Alert>
            )}
          {purchaseReqNotes && (
            <Alert severity="info" className="w-full mb-2">
              {purchaseReqNotes}
            </Alert>
          )}
          {!defaultWarehouse && (
            <div className="w-full my-2">
              <IdentityField
                name="warehouse"
                component={AutocompleteAdapter}
                options={warehouses}
                label={t('global.warehouse')}
                type="text"
                optionValue={'code'}
                setValue={setWarehouse}
                validate={required}
              />
            </div>
          )}
          {type && type['direction'] === 'IN' && !type['order_independent'] ? (
            <>
              <div className="w-full my-2">
                <IdentityField
                  name="order_code"
                  component={AutocompleteAdapter}
                  options={orders}
                  label={t('entities.movement.order_code')}
                  type="text"
                  optionValue={'code'}
                  optionLabel={'description'}
                  setValue={setOrder}
                  autoSelect={false}
                />
              </div>
            </>
          ) : null}
          <div className="w-full my-2">
            <IdentityField
              key={refresh}
              name="product"
              label={t('global.product')}
              component={AutocompleteAdapter}
              options={products}
              type="text"
              optionValue={'code'}
              optionLabel={'description'}
              setValue={setProduct}
              validate={required}
            />
          </div>
          <div className="w-full md:w-1/2 my-2 md:pr-2">
            <IdentityField
              name="movement_type"
              label={t('entities.movement.type')}
              component={AutocompleteAdapter}
              options={movementReasons}
              optionLabel="description"
              optionValue="code"
              type="text"
              setValue={setType}
              validate={required}
            />
          </div>
          <div className="w-full md:w-1/2 my-2 md:pl-2">
            <IdentityField
              name="moved_quantity"
              label={t('entities.movement.moved_quantity')}
              component={TextFieldAdapter}
              type="number"
              validate={required}
            />
          </div>
          {contractLines?.length > 1 && (
            <div className="w-full my-2">
              <IdentityField
                name="contract_line"
                label={t('global.contract')}
                component={AutocompleteAdapter}
                options={contractLines}
                type="text"
                validate={required}
              />
            </div>
          )}
          {type && type['direction'] === 'OUT' ? (
            <div className="w-full">
              <IdentityField
                key={batches.length}
                name="product_batch"
                label={t('entities.movement.product_batch')}
                component={AutocompleteAdapter}
                options={batches}
                type="text"
                validate={required}
                required
              />
            </div>
          ) : (
            <div className="w-full">
              <IdentityField
                name="product_batch"
                label={t('entities.movement.product_batch')}
                component={TextFieldAdapter}
                type="text"
              />
            </div>
          )}
          {type && type['direction'] === 'IN' ? (
            <>
              <div className="w-full my-2">
                <IdentityField
                  name="expiration_date"
                  component={DatePickerAdapter}
                  label={t('entities.movement.expiration_date')}
                  setValue={setExpiration}
                  type="date"
                />
              </div>
              <div className="w-full md:w-1/2 my-2 md:pr-2">
                <IdentityField
                  name="transport_document"
                  component={TextFieldAdapter}
                  label={t('entities.movement.transport_document')}
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 my-2 md:pl-2">
                <IdentityField
                  name="transport_document_date"
                  component={DatePickerAdapter}
                  label={t('entities.movement.transport_document_date')}
                  type="date"
                  noFuture={true}
                />
              </div>
              {locations.length > 0 && !defaultLocation && (
                <div className="w-full">
                  <IdentityField
                    name="notes_location"
                    component={AutocompleteAdapter}
                    label={t('entities.movement.notes_location')}
                    options={locations}
                    type="text"
                    optionValue={'code'}
                    autoSelect={false}
                    required={false}
                  />
                </div>
              )}
            </>
          ) : null}
          <div className="w-full my-2">
            <IdentityField
              name="notes"
              component={TextFieldAdapter}
              label={t('global.notes')}
              type="text"
            />
          </div>
          <IsTenantAdministrator>
            <div className="w-full my-2">
              <IdentityField
                name="timestamp"
                component={DateTimePickerAdapter}
                label={t(`entities.movement.timestamp`)}
                type="date"
              />
            </div>
          </IsTenantAdministrator>

          {type && type['direction'] === 'IN' ? (
            <>
              <div className="w-full my-2">
                <IdentityField
                  name="print_labels"
                  component={CheckboxAdapter}
                  label={t(`pages.movements.print_labels`)}
                  type="checkbox"
                />
              </div>
              <div className="w-full my-2">
                <IdentityField
                  name="print_barcodes"
                  component={CheckboxAdapter}
                  label={t(`pages.movements.print_barcodes`)}
                  type="checkbox"
                />
              </div>
            </>
          ) : null}
        </div>
      </EnhancedForm>

      <EnhancedDialog
        open={batchConfirmationOpen}
        onClose={handleCloseBatchConfirmation}
        onSubmit={handleConfirmSelectedBatch}
        submitLabel={t('actions.confirm', 'Conferma')}
        cancelLabel={t('actions.cancel', 'Annulla')}
        title={t('pages.movements.confirm-selected-batch-title', 'Confirm selected batch')}
        maxWidth="sm">
        <DialogContentText>
          {t('alerts.earlier-expiration', {
            batches: pendingMovementResults?.batches?.join(', ') || ''
          })}
        </DialogContentText>
        <DialogContentText sx={{ mt: 2 }}>
          {t('pages.movements.confirm-selected-batch.body')}
        </DialogContentText>
      </EnhancedDialog>
    </>
  );
}

export default ManualMovement;
